import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormGroupDirective,
  Validators
} from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxImageCompressService } from 'ngx-image-compress';
import { ViewerModalComponent } from 'ngx-ionic-image-viewer';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subscription, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { PartsRating, SeveritySelected, SeveritySelectedCurrent } from 'src/app/helpers';
import { AuthService } from 'src/app/services/auth/auth.service';
import { EstimatesService } from 'src/app/services/estimates/estimates.service';
import { LocaleService } from 'src/app/services/locale/locale.service';
import { RatingService } from 'src/app/services/rating/rating.service';
import { SharedService } from 'src/app/services/shared/shared.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-common-rating-frame',
  templateUrl: './frame.component.html',
  styleUrls: ['./frame.component.scss']
})
export class FrameComponent implements OnInit {
  public role: string = null;
  public lang: string = null;
  public loaded: boolean = false;
  public partConditionLoaded: boolean = false;
  public conditionSubmitting: boolean = false;
  public submitted: boolean = false;
  public conditionForm: UntypedFormGroup = null;

  public filesOld: object = {
    fileOld1: null,
    fileOld2: null
  };

  public filesNew: object = {
    fileNew1: null,
    fileNew2: null
  };

  public imageOld: object = {
    beforeImg1: null,
    beforeImg2: null
  };

  public imageNew: object = {
    afterImg1: null,
    afterImg2: null
  };

  public rating: string | number = null;
  public overallFrameRating: number = null;
  public overallFrameLabourCost: number = null;
  public overallFramePartCost: number = null;
  public overallFrameRepairCost: number = null;
  public vinInfo: Object = {};
  public parts: Object = {};

  public selectedSeverity: SeveritySelected = {
    exterior: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    interior: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    mechanics: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    frame: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    major_options: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    }
  };

  public allParts: any = null;
  public partSeverity: Object = {};
  public partCondition: Object[] = [];
  public frameData: Object[] = [];

  public modalRef: BsModalRef;
  public modalTitle: string = null;
  @ViewChild('condition', { static: false }) condition: TemplateRef<any>;

  public subscriptions: Subscription[] = [];

  constructor(
    public formBuilder: UntypedFormBuilder,
    public modalService: BsModalService,
    public sharedService: SharedService,
    public ratingService: RatingService,
    public estimateService: EstimatesService,
    public localeServive: LocaleService,
    public authService: AuthService,
    public toastr: ToastrService,
    private imageCompress: NgxImageCompressService,
    private modalController: ModalController
  ) {
    this.conditionForm = this.formBuilder.group({
      parts_conditions_id: [''],
      vpcs_severity: ['', [Validators.required]],
      vpcs_s_dec: [''],
      vpcs_lb_cost: [''],
      vpcs_prt_cost: [''],
      vpcs_rp_cost: [''],
      vpcs_old_image1: [''],
      vpcs_old_image2: [''],
      vpcs_new_image1: [''],
      vpcs_new_image2: ['']
    });

    this.authUser();

    this.sharedService.setTile = 'frame';
  }

  ngOnInit(): void {
    const infoSub = this.sharedService.vinInformation.subscribe((res) => {
      if (res) {
        this.vinInfo = res;
      }
    });

    const localeSub = this.localeServive.localeSubject.subscribe((res) => {
      if (res) {
        this.lang = res;
      }
    });
    this.subscriptions.push(infoSub, localeSub);

    this.mainParts('frame');
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  get formData() {
    return this.conditionForm.controls;
  }

  authUser() {
    const authSub = this.authService.user.subscribe((res) => {
      let details: Object =
        res['user'] && res['user']['user_data'] ? res['user']['user_data'] : null;

      this.role = details['role'] || 'admin';

      switch (this.role) {
        case 'evaluator':
          this.conditionForm.get('vpcs_lb_cost').clearValidators();
          this.conditionForm.get('vpcs_prt_cost').clearValidators();
          this.conditionForm.get('vpcs_rp_cost').clearValidators();
          this.conditionForm.get('vpcs_s_dec').clearValidators();
          break;
        case 'estimator':
          this.conditionForm.get('parts_conditions_id').disable();
          this.conditionForm.get('parts_conditions_id').clearValidators();
          this.conditionForm.get('vpcs_severity').clearValidators();
          this.conditionForm.get('vpcs_lb_cost').clearValidators();
          this.conditionForm.get('vpcs_prt_cost').clearValidators();
          this.conditionForm.get('vpcs_rp_cost').clearValidators();

          break;
        case 'reconditioning_manager':
          this.conditionForm.get('parts_conditions_id').disable();
          this.conditionForm.get('parts_conditions_id').clearValidators();
          this.conditionForm.get('vpcs_severity').clearValidators();
          this.conditionForm.get('vpcs_lb_cost').clearValidators();
          this.conditionForm.get('vpcs_prt_cost').clearValidators();
          this.conditionForm.get('vpcs_rp_cost').clearValidators();
          break;
        case 'qc':
          this.conditionForm.get('vpcs_lb_cost').clearValidators();
          this.conditionForm.get('vpcs_prt_cost').clearValidators();
          this.conditionForm.get('vpcs_rp_cost').clearValidators();

          this.conditionForm.get('parts_conditions_id').disable();
          this.conditionForm.get('parts_conditions_id').clearValidators();
          // this.conditionForm.get('vpcs_s_dec').clearValidators();
          break;
        case 'purchasemanager':
          this.conditionForm.get('parts_conditions_id').disable();
          this.conditionForm.get('parts_conditions_id').clearValidators();
          break;
      }

      this.conditionForm.updateValueAndValidity();
    });

    this.subscriptions.push(authSub);
  }

  mainParts(type: string) {
    let data: MainParts = {
      body_type_id: this.vinInfo['body_type_id'].id,
      parts_condition_type: type,
      parent_id: null,
      vehicle_id: this.sharedService.getVehicleId
    };

    this.loaded = true;
    const partSub = this.ratingService.getPartsCondition(data).subscribe(
      (res) => {
        let success: boolean = res['success'];
        let data: Object = res['data'];

        if (success) {
          this.loaded = false;
          this.frameData = data['ratings'];
          this.getState();
        }
      },
      (error) => {
        this.loaded = false;
      }
    );
    this.subscriptions.push(partSub);
  }

  // Getting state data
  getState() {
    let data: object = { vehicle_id: this.sharedService.getVehicleId };

    this.ratingService.getRatingStates(data).subscribe(
      (res) => {
        if (res) {
          let success: boolean = res['success'];
          let data: object = res['data'];

          if (success && Object.keys(data).length) {
            const states: SeveritySelected = data['ratingStates']
              ? JSON.parse(data['ratingStates']['rating_states'])
              : null;

            if (states && states.frame) {
              states.frame.state = states.frame.state.map((state) => {
                const matchingReview = this.frameData.find(
                  (review) => review?.['parts_condition_id'] === state.part_id
                );

                return {
                  ...state,
                  qc_review_status: matchingReview?.['qc_review_status'] ?? null
                };
              });
            }

            this.sharedService.setSeverity = states;
            this.managePartData(this.frameData);
          }
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  managePartData(data: Object[]) {
    let severityData: PartsRating[] = data.map((item: Object) => {
      if (item['part_condition']) {
        let partName: string = item['part_condition']['parts_condition_name']
          .replace(/  +/g, ' ')
          .replace(/[\s\/]/g, '_')
          .toLowerCase();
        let partId: string = item['parts_condition_id'];
        this.parts[partName] = partId;

        let Obj = {
          part_id: item['parts_condition_id'],
          part_name: item['part_condition']['parts_condition_name'],
          severity: {
            1: item['sl5'],
            2: item['sl4'],
            3: item['sl3'],
            4: item['sl2'],
            5: item['sl1']
          },
          final_severity: item['sl1'],
          rating_id: item['rating_id'],
          labour_cost: 0,
          part_cost: 0,
          repair_cost: 0
        };

        return Obj;
      }
    });
    severityData = severityData.filter((part: PartsRating) => !!part);
    this.allParts = JSON.stringify(severityData); // To solve a weird bug of value changes when assigning to global varibale.

    this.sharedService.severity.subscribe((res: SeveritySelected) => {
      if (res) {
        this.selectedSeverity = res;
      }
    });

    if (this.selectedSeverity.frame.state.length) {
      this.sharedService.setSeverity = this.selectedSeverity;
    } else {
      this.selectedSeverity.frame.state = severityData.map((item: PartsRating, index: number) => {
        let selectedSeverityLevel: number = null;

        for (let severity in item.severity) {
          if (item.severity[severity] === item.final_severity) {
            selectedSeverityLevel = +severity;
          }
        }

        return {
          severity: item.final_severity,
          severity_level: selectedSeverityLevel,
          part_id: item.part_id,
          labour_cost: +item.labour_cost,
          part_cost: +item.part_cost,
          repair_cost: +item.repair_cost
        };
      });
    }

    setTimeout(() => {
      let allSelectedSeverity: any[] = [];
      let endCorrection: object = this.selectedSeverity.frame.endCorrection;

      this.selectedSeverity.frame.state.forEach((part: SeveritySelectedCurrent, index: number) => {
        allSelectedSeverity.push(+part.severity);

        this.overallFrameLabourCost += +part.labour_cost;
        this.overallFramePartCost += +part.part_cost;
        this.overallFrameRepairCost += +part.repair_cost;
        this.severityChange(+part.severity_level, part.part_id, part?.['qc_review_status']);

        for (let factors in endCorrection) {
          if (part.part_id === endCorrection[factors].part_id) {
            this.setEndCorrectionData(
              endCorrection[factors],
              endCorrection[factors].selected_condition
            );
          }
        }
      });

      if (allSelectedSeverity.length) {
        this.overallFrameRating = allSelectedSeverity.reduce(
          (prev, curr) => +Number.parseFloat(prev + curr).toFixed(2)
        );
      }

      this.selectedSeverity.frame.overall.rating = this.overallFrameRating;
      this.selectedSeverity.frame.overall.labour_cost = this.overallFrameLabourCost;
      this.selectedSeverity.frame.overall.part_cost = this.overallFramePartCost;
      this.selectedSeverity.frame.overall.repair_cost = this.overallFrameRepairCost;
      this.sharedService.setSeverity = this.selectedSeverity;
    }, 0);
  }

  setEndCorrectionData(part: SeveritySelectedCurrent, selectedCondition?: object[]) {
    switch (part.part_id) {
      case this.parts['floor_pans']:
        this.selectedSeverity.frame.endCorrection['floor_pans'] = part;
        this.selectedSeverity.frame.endCorrection['floor_pans'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_a_pillar']:
        this.selectedSeverity.frame.endCorrection['lft_a_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['lft_a_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_apron']:
        this.selectedSeverity.frame.endCorrection['lft_apron'] = part;
        this.selectedSeverity.frame.endCorrection['lft_apron'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_b_pillar']:
        this.selectedSeverity.frame.endCorrection['lft_b_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['lft_b_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_c_pillar']:
        this.selectedSeverity.frame.endCorrection['lft_c_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['lft_c_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_frt_rail']:
        this.selectedSeverity.frame.endCorrection['lft_frt_rail'] = part;
        this.selectedSeverity.frame.endCorrection['lft_frt_rail'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_rr_d_pillar']:
        this.selectedSeverity.frame.endCorrection['lft_rr_d_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['lft_rr_d_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_rr_lock_pillar']:
        this.selectedSeverity.frame.endCorrection['lft_rr_lock_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['lft_rr_lock_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_rr_rail']:
        this.selectedSeverity.frame.endCorrection['lft_rr_rail'] = part;
        this.selectedSeverity.frame.endCorrection['lft_rr_rail'].selected_condition =
          selectedCondition;
        break;
      case this.parts['lft_strut_tower_apron']:
        this.selectedSeverity.frame.endCorrection['lft_strut_tower_apron'] = part;
        this.selectedSeverity.frame.endCorrection['lft_strut_tower_apron'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rft_a_pillar']:
        this.selectedSeverity.frame.endCorrection['rft_a_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['rft_a_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rft_apron']:
        this.selectedSeverity.frame.endCorrection['rft_apron'] = part;
        this.selectedSeverity.frame.endCorrection['rft_apron'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rft_b_pillar']:
        this.selectedSeverity.frame.endCorrection['rft_b_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['rft_b_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rft_c_pillar']:
        this.selectedSeverity.frame.endCorrection['rft_c_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['rft_c_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rht_frt_rail']:
        this.selectedSeverity.frame.endCorrection['rht_frt_rail'] = part;
        this.selectedSeverity.frame.endCorrection['rht_frt_rail'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rht_rr_d_pillar']:
        this.selectedSeverity.frame.endCorrection['rht_rr_d_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['rht_rr_d_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rht_rr_lock_pillar']:
        this.selectedSeverity.frame.endCorrection['rht_rr_lock_pillar'] = part;
        this.selectedSeverity.frame.endCorrection['rht_rr_lock_pillar'].selected_condition =
          selectedCondition;
        break;
      case this.parts['rht_rr_rail']:
        this.selectedSeverity.frame.endCorrection['rht_rr_rail'] = part;
        this.selectedSeverity.frame.endCorrection['rht_rr_rail'].selected_condition =
          selectedCondition;
        break;
    }

    // this.sharedService.setSeverity = this.selectedSeverity;
  }

  severityChange(value: number, partId: string, qc_review_status?) {
    let partElements: HTMLElement[] = Array.from(document.querySelectorAll('.partGrp'));
    this.rating = value.toFixed(1);

    partElements.forEach((elem: HTMLElement, index: number) => {
      let partIdData: string = elem.id;

      if (partIdData === partId) {
        elem.setAttribute('class', '');

        if ([0, 1].includes(qc_review_status)) {
          const tspanEl = elem.querySelector<SVGTextElement>('text tspan');

          if (tspanEl) {
            const existingText = tspanEl.textContent?.trim() || '';

            const prefix = qc_review_status === 1 ? '*' : '-';

            tspanEl.textContent = `${prefix}\u00A0${existingText}\u00A0`;
          }
        }

        if (value >= 5) {
          elem.classList.add('partGrp', 'excellent');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 4.5 && value !== 5) {
          elem.classList.add('partGrp', 'good');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 4 && value <= 4.4) {
          elem.classList.add('partGrp', 'v-fair');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 3.5 && value <= 3.999) {
          elem.classList.add('partGrp', 'fair');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 3 && value <= 3.4) {
          elem.classList.add('partGrp', 'avg');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 2.5 && value <= 2.999) {
          elem.classList.add('partGrp', 'b-avg');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 2 && value <= 2.4) {
          elem.classList.add('partGrp', 'poor');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 1.5 && value <= 1.999) {
          elem.classList.add('partGrp', 'v-poor');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 1 && value <= 1.4) {
          elem.classList.add('partGrp', 'junk');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        }
      }
    });
  }

  target: any = null;
  partId: string = null;
  filteredPartIndex: number = null;
  openCondition(evt: Event) {
    let dataTitle: string = evt.currentTarget['dataset'].name;
    let partId: string = evt.currentTarget['id'];
    this.modalTitle = dataTitle;
    this.partId = partId;
    this.target = evt.currentTarget;
    this.conditionForm.reset();

    for (let key in this.filesOld) {
      this.filesOld[key] = '';
    }

    for (let key in this.filesNew) {
      this.filesNew[key] = '';
    }

    for (let key in this.imageOld) {
      this.imageOld[key] = '';
    }

    for (let key in this.imageNew) {
      this.imageNew[key] = '';
    }

    this.getConditions(partId).then((res) => {
      setTimeout(() => {
        this.getPartsData(this.partId);

        this.modalRef = this.modalService.show(
          this.condition,
          Object.assign({}, { class: 'modal-lg optionsModal conditionModal' })
        );
      }, 0);
    });
  }

  getPartsData(partId: string) {
    let data: object = {
      vehicle_id: this.sharedService.getVehicleId,
      parent_part_id: partId
    };

    this.ratingService.getRatingPartsData(data).subscribe(
      (res) => {
        if (res) {
          let success: boolean = res['success'];
          let data: object = res['data'];

          if (success && Object.keys(data).length) {
            let partData: object = data['partsCondition'];

            if (partData) {
              partData['vpcs_old_image'] = partData['vpcs_old_image']
                ? JSON.parse(partData['vpcs_old_image'])
                : null;
              partData['vpcs_new_image'] = partData['vpcs_new_image']
                ? JSON.parse(partData['vpcs_new_image'])
                : null;

              if (partData['vpcs_old_image'] && partData['vpcs_old_image'].length) {
                for (let [key, value] of Object.entries(partData['vpcs_old_image'])) {
                  this.imageOld[`beforeImg${+key + 1}`] = value[+key + 1]
                    ? `${environment.base_path}storage/partsConditions/${value[+key + 1]}`
                    : null;
                }
              }

              if (partData['vpcs_new_image'] && partData['vpcs_new_image'].length) {
                for (let [key, value] of Object.entries(partData['vpcs_new_image'])) {
                  this.imageNew[`afterImg${+key + 1}`] = value[+key + 1]
                    ? `${environment.base_path}storage/partsConditions/${value[+key + 1]}`
                    : null;
                }
              }

              let conditionsSelected: string[] = JSON.parse(partData['vpcs_conditions']);
              this.conditionForm.get('parts_conditions_id').setValue(conditionsSelected || '');
              this.conditionForm.get('vpcs_severity').setValue(partData['vpcs_severity'] || '');
              this.conditionForm.get('vpcs_s_dec').setValue(partData['vpcs_s_dec'] || '');
              this.conditionForm.get('vpcs_lb_cost').setValue(partData['vpcs_lb_cost'] || '');
              this.conditionForm.get('vpcs_prt_cost').setValue(partData['vpcs_prt_cost'] || '');
              this.conditionForm.get('vpcs_rp_cost').setValue(partData['vpcs_rp_cost'] || '');
              this.conditionForm.get('vpcs_old_image1').setValue('');
              this.conditionForm.get('vpcs_old_image2').setValue('');
              this.conditionForm.get('vpcs_new_image1').setValue('');
              this.conditionForm.get('vpcs_new_image2').setValue('');
              this.conditionForm.updateValueAndValidity();
            } else {
              for (let key in this.imageOld) {
                this.imageOld[key] = '';
              }

              for (let key in this.imageNew) {
                this.imageNew[key] = '';
              }

              this.conditionForm.get('parts_conditions_id').setValue('');
              this.conditionForm.get('vpcs_severity').setValue('');
              this.conditionForm.get('vpcs_s_dec').setValue('');
              this.conditionForm.get('vpcs_lb_cost').setValue('');
              this.conditionForm.get('vpcs_prt_cost').setValue('');
              this.conditionForm.get('vpcs_rp_cost').setValue('');
              this.conditionForm.get('vpcs_old_image1').setValue('');
              this.conditionForm.get('vpcs_old_image2').setValue('');
              this.conditionForm.get('vpcs_new_image1').setValue('');
              this.conditionForm.get('vpcs_new_image2').setValue('');
              this.conditionForm.updateValueAndValidity();
            }
          }
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  getConditions(partId: string) {
    const data: MainParts = {
      body_type_id: this.vinInfo['body_type_id'].id,
      parts_condition_type: 'frame',
      parent_id: partId
    };
    let allParts = JSON.parse(this.allParts);
    this.filteredPartIndex = allParts.findIndex((part: PartsRating) => part.part_id === partId);

    this.partConditionLoaded = true;

    return new Promise((resolve, reject) => {
      const conditionSub = this.ratingService.getPartsCondition(data).subscribe(
        (res) => {
          let success: boolean = res['success'];
          let data: Object = res['data'];

          if (success) {
            this.partCondition = data['child_parts_conditions'];
            this.partSeverity = {
              ...allParts[this.filteredPartIndex].severity
            }; // Taking filtered severity.
            this.partConditionLoaded = false;
            resolve(this.partCondition);
          } else {
            reject(data);
            this.partConditionLoaded = false;
          }
        },
        (error) => {
          reject(error);
          this.partConditionLoaded = false;
        }
      );
      this.subscriptions.push(conditionSub);
    });
  }

  getFile(evt: Event, type: string, order: number) {
    let reader: FileReader = new FileReader();

    if (type === 'old') {
      let target: any = evt.target;
      let fileObj: File = target['files'][0];
      let fileFormatCheck: boolean =
        fileObj.type == 'image/png' ||
        fileObj.type == 'image/jpg' ||
        fileObj.type == 'image/jpeg' ||
        fileObj.type == 'image/webp';

      if (fileFormatCheck) {
        // this.fileOld = fileObj;
        reader.readAsDataURL(fileObj);
        reader.onload = (ev) => {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              const image: any = ev.target.result;
              this.imageCompression(image, fileObj.name, type, order);
            }
          }
        };
      } else return false;
    } else if (type === 'new') {
      let target: any = evt.target;
      let fileObj: File = target['files'][0];
      let fileFormatCheck: boolean =
        fileObj.type == 'image/png' ||
        fileObj.type == 'image/jpg' ||
        fileObj.type == 'image/jpeg' ||
        fileObj.type == 'image/webp';

      if (fileFormatCheck) {
        // this.fileNew = fileObj;
        reader.readAsDataURL(fileObj);
        reader.onload = (ev) => {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              const image: any = ev.target.result;
              this.imageCompression(image, fileObj.name, type, order);
            }
          }
        };
      } else return false;
    }

    setTimeout(() => {
      evt.target['value'] = '';
    }, 0);
  }

  imageCompression(base64: string, fileName: string, type: string, order: number) {
    this.imageCompress.compressFile(base64, -1, 60, 50).then((res) => {
      if (type === 'old') {
        for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
          if (index === order) {
            const imageBlobOld = this.sharedService.dataURItoBlob(res.split(',')[1]);
            this.filesOld[`fileOld${order}`] = new File([imageBlobOld], fileName, {
              type: 'image/jpeg'
            });
            this.imageOld[`beforeImg${order}`] = null;
          }
        }
      } else {
        for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
          if (index === order) {
            const imageBlobNew = this.sharedService.dataURItoBlob(res.split(',')[1]);
            this.filesNew[`fileNew${order}`] = new File([imageBlobNew], fileName, {
              type: 'image/jpeg'
            });
            this.imageNew[`afterImg${order}`] = null;
          }
        }
      }
    });
  }

  async openViewer(imageURL: string) {
    const modal = await this.modalController.create({
      component: ViewerModalComponent,
      componentProps: {
        src: imageURL,
        scheme: 'dark'
      },
      cssClass: 'ion-img-viewer',
      keyboardClose: true,
      showBackdrop: true
    });

    return await modal.present();
  }

  removeImage(image: string | File, type: string, order: number) {
    if (type === 'old') {
      setTimeout(() => {
        // API call to reset
        if (typeof image === 'string') {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              this.imageDeleteReq(image);
              this.imageOld[`beforeImg${order}`] = null;
            }
          }
        } else {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              this.filesOld[`fileOld${order}`] = null;
            }
          }
        }
      }, 0);
    } else {
      setTimeout(() => {
        // API call to reset
        if (typeof image === 'string') {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              this.imageDeleteReq(image);
              this.imageNew[`afterImg${order}`] = null;
            }
          }
        } else {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              this.filesNew[`fileNew${order}`] = null;
            }
          }
        }
      }, 0);
    }
  }

  imageDeleteReq(imageName: string) {
    imageName = imageName.replace('/partsConditions/', ' ');
    imageName = imageName.slice(imageName.indexOf(' ')).trim();

    const data: object = {
      vehicle_id: this.sharedService.getVehicleId,
      parent_part_id: this.partId,
      image: imageName
    };

    this.ratingService.deleteRatingImage(data).subscribe(
      (res) => {},
      (error: HttpErrorResponse) => {}
    );
  }

  saveConditionState() {
    let conditionFormData: FormData = new FormData();
    let allParts: PartsRating[] = JSON.parse(this.allParts);
    let costOfRepair: any =
      +this.conditionForm.get('vpcs_lb_cost').value +
        +this.conditionForm.get('vpcs_prt_cost').value || '';
    this.conditionForm.get('vpcs_rp_cost').setValue(costOfRepair.toString());

    allParts[this.filteredPartIndex].final_severity =
      this.partSeverity[this.conditionForm.get('vpcs_severity').value];
    allParts[this.filteredPartIndex].repair_cost = this.conditionForm.get('vpcs_rp_cost').value;

    // Updating the the selected part state data
    // this.selectedSeverity.frame.state[this.filteredPartIndex].part_id = allParts[this.filteredPartIndex].part_id;
    this.selectedSeverity.frame.state[this.filteredPartIndex].severity =
      allParts[this.filteredPartIndex].final_severity;
    this.selectedSeverity.frame.state[this.filteredPartIndex].severity_level =
      +this.conditionForm.get('vpcs_severity').value;
    this.selectedSeverity.frame.state[this.filteredPartIndex].labour_cost =
      +this.conditionForm.get('vpcs_lb_cost').value;
    this.selectedSeverity.frame.state[this.filteredPartIndex].part_cost =
      +this.conditionForm.get('vpcs_prt_cost').value;
    this.selectedSeverity.frame.state[this.filteredPartIndex].repair_cost =
      +allParts[this.filteredPartIndex].repair_cost;

    this.submitted = true;

    if (this.conditionForm.invalid) {
      return false;
    } else {
      let severityVal: number = +this.conditionForm.get('vpcs_severity').value;

      conditionFormData.set('vehicle_id', this.sharedService.getVehicleId);
      conditionFormData.set('parent_part_id', this.partId);
      conditionFormData.set(
        'parts_conditions_id',
        JSON.stringify(this.conditionForm.get('parts_conditions_id').value)
      );
      conditionFormData.set('vpcs_severity', this.conditionForm.get('vpcs_severity').value);
      conditionFormData.set('vpcs_s_dec', this.conditionForm.get('vpcs_s_dec').value);
      conditionFormData.set('vpcs_lb_cost', this.conditionForm.get('vpcs_lb_cost').value);
      conditionFormData.set('vpcs_prt_cost', this.conditionForm.get('vpcs_prt_cost').value);
      conditionFormData.set('vpcs_rp_cost', this.conditionForm.get('vpcs_rp_cost').value);
      conditionFormData.set('vpcs_rp_cost', this.conditionForm.get('vpcs_rp_cost').value);
      conditionFormData.set('vpcs_old_image1', this.filesOld['fileOld1']);
      conditionFormData.set('vpcs_old_image2', this.filesOld['fileOld2']);
      conditionFormData.set('vpcs_new_image1', this.filesNew['fileNew1']);
      conditionFormData.set('vpcs_new_image2', this.filesNew['fileNew2']);

      this.conditionSubmitting = true;
      this.ratingService
        .setPartsCondition(conditionFormData)
        .pipe(
          switchMap((res) => {
            if (!res['success']) {
              this.toastr.error('Something went wrong', 'Failed');
              return EMPTY;
            }

            return this.role === 'evaluator'
              ? this.estimateService.updateRevamCount({
                  vehicle_id: this.sharedService.getVehicleId
                })
              : of(null);
          }),
          tap(() => {
            let conditionSelected: string[] = this.conditionForm.get('parts_conditions_id').value;
            let selectedCondition: object[] = [];
            let allSelectedSeverityUpdated: any[] = [];

            this.overallFrameRating = null;
            this.overallFrameLabourCost = null;
            this.overallFramePartCost = null;
            this.overallFrameRepairCost = null;

            this.selectedSeverity.frame.state.forEach(
              (part: SeveritySelectedCurrent, index: number) => {
                allSelectedSeverityUpdated.push(+part.severity);
                this.overallFrameLabourCost += +part.labour_cost;
                this.overallFramePartCost += +part.part_cost;
                this.overallFrameRepairCost += +part.repair_cost;
              }
            );

            this.overallFrameRating = allSelectedSeverityUpdated.reduce((prev, curr) => {
              return +Number.parseFloat(prev + curr).toFixed(2);
            });

            this.selectedSeverity.frame.overall.rating = this.overallFrameRating;
            this.selectedSeverity.frame.overall.labour_cost = this.overallFrameLabourCost;
            this.selectedSeverity.frame.overall.part_cost = this.overallFramePartCost;
            this.selectedSeverity.frame.overall.repair_cost = this.overallFrameRepairCost;

            if (conditionSelected.length) {
              conditionSelected.forEach((condition: string) => {
                selectedCondition.push(
                  ...this.partCondition.filter(
                    (item: object) => item['parts_condition_id'] === condition
                  )
                );
              });
            }

            this.setEndCorrectionData(
              this.selectedSeverity.frame.state[this.filteredPartIndex],
              selectedCondition
            );
            this.saveStateData(this.selectedSeverity);
            this.sharedService.setSeverity = this.selectedSeverity;

            for (let severity in this.partSeverity) {
              if (severity == this.conditionForm.get('vpcs_severity').value) {
                this.severityChange(+severity, this.partId);
                this.modalRef.hide();

                for (let key in this.filesOld) {
                  this.filesOld[key] = null;
                }

                for (let key in this.filesNew) {
                  this.filesNew[key] = null;
                }

                this.conditionForm.get('parts_conditions_id').setValue('');
                this.conditionForm.get('vpcs_severity').setValue('');
                this.conditionForm.get('vpcs_s_dec').setValue('');
                this.conditionForm.get('vpcs_lb_cost').setValue('');
                this.conditionForm.get('vpcs_prt_cost').setValue('');
                this.conditionForm.get('vpcs_rp_cost').setValue('');
                this.conditionForm.updateValueAndValidity();
                this.submitted = false;
              }
            }

            this.toastr.success('Rating added successfully', 'Success');
          }),
          finalize(() => {
            this.conditionSubmitting = false;
          })
        )
        .subscribe({
          error: () => {
            this.toastr.error('Something went wrong', 'Failed');
          }
        });
    }
  }

  // Saving rating state data to DB/locally
  saveStateData(state: SeveritySelected) {
    let data: object = {
      rating_states_id: null,
      vehicle_id: this.sharedService.getVehicleId,
      rating_states: JSON.stringify(state)
    };

    this.ratingService.setRatingStates(data).subscribe(
      (res) => {
        if (res) {
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }
}
interface MainParts {
  body_type_id: string;
  parts_condition_type: string;
  parent_id?: string;
  vehicle_id?: string;
}
